import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { UploadCloud, Trash2, RefreshCw, Loader2, FileImage } from "lucide-react";
import { toast } from "sonner";
import { uploadFile, apiFetch } from "@/lib/api";
import { cn } from "@/lib/utils";

const ACCEPT = ".png,.jpg,.jpeg,.webp,.ico,.svg,image/png,image/jpeg,image/webp,image/x-icon,image/svg+xml";
const MAX = 5 * 1024 * 1024;

function fmtSize(bytes) {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }); }
  catch { return ""; }
}

// Reusable branding/image upload card. dark = render preview on a dark surface.
export function ImageUpload({ label, hint, assetType, current, dark = false, onChange }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);

  const pick = () => inputRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > MAX) return toast.error("That file is larger than the 5 MB limit.");
    setBusy(true); setProgress(35);
    try {
      const res = await uploadFile(`/branding/${assetType}`, file);
      setProgress(100);
      toast.success(`${label} updated`);
      onChange?.(res.asset);
    } catch (err) { toast.error(err.message); }
    finally { setBusy(false); setTimeout(() => setProgress(0), 400); }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await apiFetch(`/branding/${assetType}`, { method: "DELETE" });
      toast.success(`${label} reset to default`);
      onChange?.(null);
    } catch (err) { toast.error(err.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl border border-border p-4" data-testid={`branding-${assetType}`}>
      <div className="mb-1 text-sm font-medium">{label}</div>
      {hint && <div className="mb-3 text-xs text-muted-foreground">{hint}</div>}
      <div className={cn(
        "mb-3 flex h-24 items-center justify-center overflow-hidden rounded-lg border border-dashed border-border p-2",
        dark ? "bg-[#0f1f18]" : "bg-[#FBF8F1]"
      )}>
        {current?.url
          ? <img src={current.url} alt={label} className="max-h-full max-w-full object-contain" data-testid={`branding-${assetType}-preview`} />
          : <span className={cn("text-xs", dark ? "text-white/50" : "text-muted-foreground")}>Nirvana default</span>}
      </div>
      {current?.filename && (
        <div className="mb-3 flex items-start gap-2 rounded-md bg-muted/50 px-2.5 py-1.5" data-testid={`branding-${assetType}-meta`}>
          <FileImage className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <div className="min-w-0 text-[11px] leading-tight text-muted-foreground">
            <div className="truncate font-medium text-foreground">{current.filename}</div>
            <div>{[fmtSize(current.size), fmtDate(current.uploaded_at)].filter(Boolean).join(" · ")}</div>
          </div>
        </div>
      )}
      {busy && progress > 0 && <Progress value={progress} className="mb-3 h-1.5" />}
      <input ref={inputRef} type="file" accept={ACCEPT} className="hidden" onChange={onFile}
        data-testid={`branding-${assetType}-input`} />
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" onClick={pick} disabled={busy} data-testid={`branding-${assetType}-upload`}>
          {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
            : current?.url ? <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> : <UploadCloud className="mr-1.5 h-3.5 w-3.5" />}
          {current?.url ? "Replace" : "Upload"}
        </Button>
        {current?.url && (
          <Button size="sm" variant="ghost" onClick={remove} disabled={busy}
            className="text-destructive hover:text-destructive" data-testid={`branding-${assetType}-remove`}>
            <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Reset
          </Button>
        )}
      </div>
    </div>
  );
}
